import { useMemo } from "react"

import { cn } from "@productlane/lib/src/cn"
import {
	AttachmentsList,
	formatSlackAttachments,
	typographyVariants,
} from "@productlane/ui"

import type { TimelineEventProps } from "./types"
import { sanitizeMessageHtml } from "@/lib/sanitize-message-html"
import { useThreadContext } from "../../../thread-context"
import { AgentMessageAvatar } from "./base/agent-message-avatar"
import { SlackStyleMessage } from "./base/slack-style-message"
import { DeviceInfoLine } from "./device-info-line"
import { AI_CHAT_ACTOR_NAME } from "./improve-reply"
import { ImproveReplyButton } from "./improve-reply-panel"
import { AddReactionButton, MessageReactions } from "./message-reactions"
import { SlackDeliveryNotice } from "./slack-delivery-notice"

export function SlackMessageEvent({
	event,
	showAvatar = true,
	deviceInfoText,
}: TimelineEventProps) {
	const { thread } = useThreadContext()

	const companyLogoUrl = thread.company?.logoUrl || undefined
	const companyName = thread.company?.name || undefined

	const attachments = useMemo(() => {
		if (event.type !== "slackMessage") return []
		const attachmentsValue = event.slackMessage.attachments
		if (!attachmentsValue || !Array.isArray(attachmentsValue)) return []
		return attachmentsValue as unknown[]
	}, [event])

	const formattedAttachments = useMemo(
		() => formatSlackAttachments(attachments),
		[attachments],
	)

	if (event.type !== "slackMessage") {
		return null
	}

	return (
		<div className="group/reactable">
			<SlackStyleMessage
				showAvatar={showAvatar}
				senderName={
					event.isFromAgent
						? AI_CHAT_ACTOR_NAME
						: event.slackMessage.fromContactName
				}
				senderEmail={event.slackMessage.fromContactEmail}
				senderImage={event.slackMessage.fromContactImage}
				timestamp={new Date(event.slackMessage.createdAt)}
				timestampLink={event.slackMessage.slackLink}
				companyName={companyName}
				companyLogoUrl={companyLogoUrl}
				source="slack"
				isOutbound={event.slackMessage.type === "OUTBOUND"}
				avatarContent={event.isFromAgent ? <AgentMessageAvatar /> : undefined}
				actions={
					<AddReactionButton
						threadId={event.slackMessage.threadId}
						messageType="slack"
						messageId={event.slackMessage.id}
					/>
				}
				footer={
					<>
						<MessageReactions
							threadId={event.slackMessage.threadId}
							messageType="slack"
							messageId={event.slackMessage.id}
						/>
						{deviceInfoText ? <DeviceInfoLine text={deviceInfoText} /> : null}
						<SlackDeliveryNotice
							message={{
								id: event.slackMessage.id,
								type: event.slackMessage.type,
								slackId: event.slackMessage.slackId,
								createdAt: Number(event.slackMessage.createdAt),
							}}
						/>
					</>
				}
			>
				<div
					dangerouslySetInnerHTML={{
						__html: sanitizeMessageHtml(event.slackMessage.html),
					}}
					className={cn(
						"html-content",
						typographyVariants({
							variant: "message",
							className: "min-w-full overflow-auto",
						}),
					)}
				/>
				{formattedAttachments.length > 0 && (
					<>
						{event.slackMessage.html && <hr className="my-3" />}
						<AttachmentsList
							attachments={formattedAttachments}
							enableViewer
							showPointer={false}
						/>
					</>
				)}
				{event.isAgentReply && (
					<div className="mt-2">
						<ImproveReplyButton
							threadId={thread.id}
							channel="slack"
							messageId={event.slackMessage.id}
						/>
					</div>
				)}
			</SlackStyleMessage>
		</div>
	)
}
